import {
  type GroupTypeEnum,
  GET_GROUP_CLASS_PAGE_SIZE_V2,
  GroupTypeTagColorMapper,
} from '~/lib/constants/groupClasses.constants';
import urlConstants from '~/lib/constants/url.constants';
import { joinQueryParams } from '~/lib/util/api';
import { CacheTag } from '~/store/services/CacheTag';

import { type GroupClassIllustrations, TagColor } from '@equip.health/ui';
import type { Recurrence } from '~/lib/util/calendar.util';
import { ContentTypes, HttpHeaders, HttpRequestMethod } from '..';
import api from './api';
import { enumerationApi } from './enumerations';
import { formatTimeRange } from '~/lib/util/date.util';
import type { TreatmentModalityEnumeration } from '~/lib/types/treatmentAttributes/treatmentAttributes';
import {
  formatGroupSectionRecurrence,
  enrichGroupListWithEnumerations,
  getFormattedDurationWeeks,
  formattedAgeRange,
  enrichGroupWithEnumerations,
  generateHostInitials,
} from '~/lib/util/groups.utils';
import type { SectionItem } from '~/components/maud/calendar/groupsV2/sectionsList';

const {
  groupClasses: {
    getGroupClassEventMeetingDetails: getGroupClassEventMeetingDetailsUrl,
    getGroupSectionEventMeetingDetailsV2:
      getGroupSectionEventMeetingDetailsV2Url,
    addHosts: addHostsUrl,
    cancelGroupClassEvent: cancelEventUrl,
    declineGroupClassEvent: declineEventUrl,
    updateGroupSectionEventV2: updateGroupSectionEventV2Url,
  },
} = urlConstants;

interface UpdateGroupSectionEventV2Request {
  groupEventExternalId: string;
  hosts: string[];
  isCancel: boolean;
  isDecline: boolean;
}

interface GroupClassArchiveRequest {
  groupClassExternalId: string;
  archiveReasonId: string;
}

interface RegisterForGroupClassRequest {
  groupClassExternalId: string;
}

interface CreateGroupClassRequest {
  description: string;
  duration: number;
  endDate: Date;
  frequency: Recurrence;
  hosts: string[];
  iconName: GroupClassIllustrations;
  isSensitive: boolean;
  maximumAttendees: number;
  mustAttendAll: boolean;
  registrationEnds: Date;
  startDate: Date;
  title: string;
  treatmentPrograms?: string[];
  treatmentModalities?: string[];
  diagnoses?: string[];
  ageRanges?: string[];
  isPromoted: boolean;
  attendeeRoles?: string[];
}

interface EditGroupClassRequest {
  description: string;
  endDate: Date;
  frequency: Recurrence;
  groupClassExternalId: string;
  hostExternalIds: string[];
  isSensitive: boolean;
  title: string;
  isPromoted: boolean;
  mustAttendAll?: boolean;
  treatmentPrograms?: string[];
  treatmentModalities?: string[];
  diagnoses?: string[];
  ageRanges?: string[];
  attendeeRoles?: string[];
}

interface CreateGroupClassResponse {
  status: string;
  data: string;
}

interface GroupStatusOptionsResponse {
  code: GroupStatusType;
  status: string;
  displayOrder: number;
}

interface GroupSection {
  id: string;
  frequency: SectionFrequencyCode;
  duration: number;
  startDate: string;
  endDate: string;
  sessionCode: string;
  noOfRegistrants: number;
  hosts: HostInput[];
  isWebinar: boolean;
  mustAttendAll: boolean;
  maximumAttendees: number;
}

export interface GroupDetailsResponse {
  ageRanges: string[];
  attendeeRoles: string[];
  core: boolean;
  description: string;
  diagnoses: string[];
  groupTypeCode: GroupTypeEnum;
  groupType?: string;
  groupTypeTagColor?: TagColor;
  groupStatusCode: GroupStatusType;
  iconName: GroupClassIllustrations;
  id: string;
  noOfSections?: number;
  sensitive: boolean;
  sections: GroupSection[];
  title: string;
  treatmentPrograms: string[];
  treatmentModalities: string[];
}

interface GroupDetailsV2Response extends GroupDetailsResponse {
  groupType: string;
  ageRange: string[];
  sectionsCount: number;
  isSensitive: boolean;
}

export interface GroupClassFrequencyOption {
  code: string;
  frequency: string;
  displayOrder: number;
}

export interface GroupClassSessionOption {
  code: string;
  noOfSessions: number;
  displayName: string;
  displayOrder: number;
}
export interface GetRegistrantsV2Response {
  externalId: string;
  firstName: string;
  lastName: string;
  chosenName: string;
  type: string;
  isAttended: boolean;
  isDeclined: boolean;
  supportPatientLinks: SupportPatientDetails[];
}

export const GROUP_CLASS_CACHE_TIME = 500 * 60;

type RawSectionListItem = {
  id: string;
  groupId: string;
  title: string;
  iconName: SectionItem['iconName'];
  groupType?: GroupType;
  groupStatus?: string;
  frequency?: SectionFrequencyCode;
  duration?: number;
  startDate?: string;
  sessionCode?: string;
  hosts?: ScheduleUserBase[];
  noOfSections?: number;
  sensitive?: boolean;
};

interface GetGroupSectionEventDatesRequest {
  startDate: string;
  endDate: string;
  providerExternalId: string;
  patientExternalId: string;
}

type WithProviderIdOnly = Omit<
  GetGroupSectionEventDatesRequest,
  'patientExternalId'
> & {
  patientExternalId?: never;
};

type WithPatientIdOnly = Omit<
  GetGroupSectionEventDatesRequest,
  'providerExternalId'
> & {
  providerExternalId?: never;
};

type GetGroupSectionEventDatesParams = WithProviderIdOnly | WithPatientIdOnly;

export const groupClassApi = api.injectEndpoints({
  endpoints: (build) => ({
    addHosts: build.mutation<
      void,
      { hostExternalIds: string[]; groupClassEventExternalId: string }
    >({
      invalidatesTags: [CacheTag.GroupHosts],
      query: ({ hostExternalIds, groupClassEventExternalId }) => ({
        body: { hostExternalIds },
        method: 'PATCH',
        url: addHostsUrl(groupClassEventExternalId),
      }),
    }),
    archiveGroupClass: build.mutation<void, GroupClassArchiveRequest>({
      invalidatesTags: [CacheTag.GroupClassCounts],
      query: ({ groupClassExternalId, archiveReasonId }) => ({
        body: { archiveReasonId },
        method: HttpRequestMethod.PATCH,
        responseHandler: 'text',
        url: urlConstants.groupClasses.archiveGroupClass(groupClassExternalId),
      }),
    }),
    cancelEvent: build.mutation<void, string>({
      invalidatesTags: [CacheTag.GroupClassEvents],
      query: (groupClassEventExternalId) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.POST,
        url: cancelEventUrl(groupClassEventExternalId),
      }),
    }),
    cancelGroupEvent: build.mutation<void, { groupClassExternalId: string }>({
      query: ({ groupClassExternalId }) => ({
        method: HttpRequestMethod.POST,
        url: urlConstants.groupClasses.cancelGroupClassEvent(
          groupClassExternalId,
        ),
      }),
    }),

    createGroupClass: build.mutation<
      CreateGroupClassResponse,
      CreateGroupClassRequest
    >({
      invalidatesTags: [CacheTag.GroupClassCounts],
      query: (groupClassRequest) => ({
        body: groupClassRequest,
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.POST,
        url: urlConstants.calendar.createGroupClass,
      }),
    }),

    editGroupClass: build.mutation<
      CreateGroupClassResponse,
      EditGroupClassRequest
    >({
      invalidatesTags: [CacheTag.GroupClassCounts, CacheTag.GroupClassList],
      query: (groupClassRequest) => ({
        body: groupClassRequest,
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.PATCH,
        url: urlConstants.calendar.updateGroupClass(
          groupClassRequest?.groupClassExternalId,
        ),
      }),
    }),

    declineEvent: build.mutation<void, string>({
      invalidatesTags: [CacheTag.GroupClassEvents],
      query: (groupClassEventExternalId) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.POST,
        url: declineEventUrl(groupClassEventExternalId),
      }),
    }),
    updateGroupSectionEventV2: build.mutation<
      void,
      UpdateGroupSectionEventV2Request
    >({
      invalidatesTags: [
        CacheTag.GroupClassEvents,
        CacheTag.GroupClassEventsV2,
        CacheTag.GroupHosts,
      ],
      query: ({ groupEventExternalId, hosts, isCancel, isDecline }) => ({
        body: {
          hosts,
          isCancel,
          isDecline,
        },
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.PATCH,
        url: updateGroupSectionEventV2Url(groupEventExternalId),
      }),
    }),
    getActiveOrArchivedGroupClassesList: build.query<
      { data: GroupClassBaseV2[] },
      { isActive: boolean; pageNumber: number; title?: string }
    >({
      query: ({ isActive, pageNumber, title }) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        params: {
          'is-active': isActive,
          'page-number': pageNumber,
          'page-size': GET_GROUP_CLASS_PAGE_SIZE_V2,
          title,
        },
        url: urlConstants.groupClasses.getActiveOrArchiveGroupClasses,
      }),
    }),

    getActiveOrArchiveGroupsV2: build.query<
      EnrichedGroupList[],
      { isActive: boolean; pageNumber: number; title?: string }
    >({
      queryFn: async (
        { isActive, pageNumber, title },
        { dispatch },
        _extraOptions,
        baseQuery,
      ) => {
        const mainResult = await baseQuery({
          headers: {
            [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
          },
          params: {
            'is-active': isActive,
            'page-number': pageNumber,
            'page-size': GET_GROUP_CLASS_PAGE_SIZE_V2,
            title,
          },
          url: urlConstants.groupClasses.getActiveOrArchiveGroupsV2,
        });

        if (mainResult.error) return { error: mainResult.error };

        const [programsResult, modalitiesResult, groupTypesResult] =
          await Promise.all([
            dispatch(
              enumerationApi.endpoints.getTreatmentPrograms.initiate(
                undefined,
                { forceRefetch: false, subscribe: false },
              ),
            ),
            dispatch(
              enumerationApi.endpoints.getTreatmentModalitiesEnum.initiate(
                undefined,
                { forceRefetch: false, subscribe: false },
              ),
            ),
            dispatch(
              enumerationApi.endpoints.getGroupTypesEnum.initiate(undefined, {
                forceRefetch: false,
                subscribe: false,
              }),
            ),
          ]);

        const programs: Enumeration[] = programsResult.data ?? [];
        const modalities: TreatmentModalityEnumeration[] =
          modalitiesResult.data ?? [];
        const groupTypes: GroupTypeResponse[] = groupTypesResult.data ?? [];

        const rawList = (mainResult.data as GroupList[]) ?? [];
        const enriched: EnrichedGroupList[] = enrichGroupListWithEnumerations(
          rawList,
          programs,
          modalities,
          groupTypes,
        );

        return { data: enriched };
      },
    }),

    getMySectionsList: build.query<
      SectionItem[],
      { pageNumber: number; title?: string }
    >({
      queryFn: async (
        { pageNumber, title },
        { dispatch },
        _extra,
        baseQuery,
      ) => {
        const mainResult = await baseQuery({
          url: urlConstants.groupClasses.getMySectionsListUrl,
          params: {
            'page-number': pageNumber,
            'page-size': GET_GROUP_CLASS_PAGE_SIZE_V2,
            title,
          },
        });

        if (mainResult.error) return { error: mainResult.error };

        const groupTypesResult = await dispatch(
          enumerationApi.endpoints.getGroupTypesEnum.initiate(undefined, {
            forceRefetch: false,
            subscribe: false,
          }),
        );

        const groupTypes: GroupTypeResponse[] = groupTypesResult.data ?? [];

        const rawSectionsList = (mainResult.data as RawSectionListItem[]) ?? [];

        const formattedSectionsList: SectionItem[] = rawSectionsList.map(
          (item) => {
            const group = groupTypes.find((gt) => gt.code === item.groupType);

            return {
              id: item.id,
              groupId: item.groupId,
              title: item.title,
              iconName: item.iconName,
              groupType: group.type,
              groupTypeTagColor:
                GroupTypeTagColorMapper[group.code] ?? TagColor.gray,
              groupStatusCode: item.groupStatus,
              frequency: item.frequency,
              noOfSections: item.noOfSections ?? 0,
              sensitive: item.sensitive ?? false,
              schedule:
                formatGroupSectionRecurrence({
                  frequency: item.frequency,
                  start: item.startDate,
                }) ?? '',
              timeSlot: formatTimeRange(item.startDate, item.duration),
              hosts: (item.hosts ?? []).map((host) => ({
                id: host.externalId,
                name: generateHostInitials(host),
                src: host.avatar?.thumbnailUrl ?? host.avatar?.url ?? '',
                userType: (host.type ?? 'provider') as any,
              })),
              durationWeeks: getFormattedDurationWeeks(item.sessionCode),
              startDate: item.startDate,
            };
          },
        );

        return { data: formattedSectionsList };
      },
    }),

    getArchiveReasonsList: build.query<GroupClassArchivalReasonResponse, void>({
      query: () => urlConstants.groupClasses.archiveReasons,
    }),

    getGroupClassCounts: build.query<{ data: GroupClassCountResponse }, void>({
      providesTags: [CacheTag.GroupClassCounts],
      query: () => urlConstants.groupClasses.getGroupClassCount,
    }),

    getGroupClassCountsV2: build.query<GroupClassCountV2Response, void>({
      providesTags: [CacheTag.GroupCountV2],
      query: () => urlConstants.groupClasses.getGroupClassCountV2,
    }),

    getGroupClassDetail: build.query<
      { data: GroupClassExtended },
      { groupClassExternalId: string }
    >({
      query: ({ groupClassExternalId }) => ({
        url: urlConstants.groupClasses.getGroupClass(groupClassExternalId),
      }),
    }),
    // TODO fix: This type is not perfectly accurate to the API response, but unraveling is tricky as it's frequently used
    getGroupClassEventDetails: build.query<GroupClassEventDetails, string>({
      query: (groupClassExternalId) => ({
        url: urlConstants.groupClasses.getGroupClassEventDetails(
          groupClassExternalId,
        ),
      }),
      transformResponse: (response: BaseAPIResponse<GroupClassEventDetails>) =>
        response?.data,
      keepUnusedDataFor: 5 * 60,
      providesTags: (_, __, groupClassEventExternalId) => [
        {
          id: groupClassEventExternalId,
          type: CacheTag.GroupClassEventDetails,
        },
        CacheTag.GroupHosts,
      ],
    }),
    getGroupClassEventMeetingDetails: build.mutation<ZoomUrlResponse, string>({
      query: (groupClassEventExternalId) => ({
        body: {},
        method: HttpRequestMethod.POST,
        url: getGroupClassEventMeetingDetailsUrl(groupClassEventExternalId),
      }),
    }),
    getGroupSectionEventMeetingDetailsV2: build.mutation<
      MeetingDetails,
      string
    >({
      query: (groupSectionEventExternalId) => ({
        body: {},
        method: HttpRequestMethod.POST,
        url: getGroupSectionEventMeetingDetailsV2Url(
          groupSectionEventExternalId,
        ),
      }),
    }),

    getGroupClassEvents: build.query<
      FetchGroupClassEventsResponse['data'],
      {
        startDateTime: string;
        endDateTime?: string;
        pageSize?: number;
        patientExternalId?: string;
        providerExternalId?: string;
        showDetailed?: boolean;
      }
    >({
      query: ({
        startDateTime,
        endDateTime,
        pageSize = 50,
        patientExternalId,
        providerExternalId,
        showDetailed,
      }) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        params: {
          ['filter-by']: joinQueryParams({
            ['start-date']: startDateTime,
            ...(endDateTime && { ['end-date']: endDateTime }),
          }),
          ['page-size']: pageSize,
          ['patient-external-id']: patientExternalId,
          ['provider-external-id']: providerExternalId,
          ['show-detailed']: showDetailed,
        },
        url: urlConstants.groupClasses.getGroupClassEvents,
      }),
      transformResponse: (response: FetchGroupClassEventsResponse) =>
        response?.data,
      providesTags: [CacheTag.GroupClassEvents],
    }),

    getGroupClassEventConfirmedAttendees: build.query<
      GroupClassEventAttendee[],
      string
    >({
      query: (groupClassEventExternalId) => ({
        url: urlConstants.groupClasses.getGroupClassEventConfirmedAttendees(
          groupClassEventExternalId,
        ),
      }),
      keepUnusedDataFor: 5 * 60,
      transformResponse: (
        response: BaseAPIResponse<GroupClassEventAttendee[]>,
      ) => response?.data,
      providesTags: (_, __, groupClassEventExternalId) => [
        {
          type: CacheTag.GroupClassEventConfirmedAttendees,
          id: groupClassEventExternalId,
        },
      ],
    }),
    getMyGroupClassesList: build.query<
      { data: GroupClassBaseV2[] },
      { pageNumber: number; title?: string }
    >({
      query: ({ pageNumber, title }) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        params: {
          'page-number': pageNumber,
          'page-size': GET_GROUP_CLASS_PAGE_SIZE_V2,
          title,
        },
        url: urlConstants.groupClasses.getYourGroupClasses,
      }),
    }),
    updateGroupClassEventAttendees: build.mutation<
      { status: string },
      { groupClassEventExternalId: string; attendees: AttendeeItem[] }
    >({
      query: ({ groupClassEventExternalId, attendees }) => ({
        method: 'PATCH',
        url: urlConstants.groupClasses.modifyGroupClassEventAttendees(
          groupClassEventExternalId,
        ),
        body: attendees,
      }),
      invalidatesTags: (result, _, arg) =>
        result
          ? [
              {
                type: CacheTag.GroupClassEventConfirmedAttendees,
                id: arg.groupClassEventExternalId,
              },
              {
                type: CacheTag.GroupClassEventDetails,
                id: arg.groupClassEventExternalId,
              },
            ]
          : [],
    }),

    updateGroupSectionEventAttendees: build.mutation<
      { status: string },
      { groupSectionEventExternalId: string; attendees: AttendeeItem[] }
    >({
      query: ({ groupSectionEventExternalId, attendees }) => ({
        method: 'PATCH',
        url: urlConstants.groupClasses.getGroupEventRegistrants(
          groupSectionEventExternalId,
        ),
        body: attendees,
      }),
      invalidatesTags: (result, _, arg) =>
        result
          ? [
              {
                type: CacheTag.GroupSectionEventConfirmedAttendees,
                id: arg.groupSectionEventExternalId,
              },
            ]
          : [],
    }),
    removeGroupClassEvent: build.mutation<void, string>({
      queryFn: (groupClassEventExternalId, { dispatch }) => {
        dispatch(
          groupClassApi.util.updateQueryData(
            'getGroupClassEvents',
            undefined,
            (draft) => {
              const index = draft?.data?.findIndex(
                (event) =>
                  event.groupClassEventExternalId === groupClassEventExternalId,
              );
              if (index !== -1) {
                draft.data.splice(index, 1);
              }
            },
          ),
        );
        return { data: undefined };
      },
    }),
    registerForGroupClass: build.mutation<void, RegisterForGroupClassRequest>({
      invalidatesTags: [CacheTag.GroupClassList, CacheTag.GroupClassCounts],
      query: ({ groupClassExternalId }) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.POST,
        url: urlConstants.groupClasses.registerGroupClass(groupClassExternalId),
      }),
    }),

    unregisterForGroupClass: build.mutation<void, RegisterForGroupClassRequest>(
      {
        invalidatesTags: [CacheTag.GroupClassList, CacheTag.GroupClassCounts],
        query: ({ groupClassExternalId }) => ({
          headers: {
            [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
          },
          method: HttpRequestMethod.POST,
          url: urlConstants.groupClasses.unRegisterGroupClass(
            groupClassExternalId,
          ),
        }),
      },
    ),

    createGroupV2: build.mutation<string, GroupFormState>({
      invalidatesTags: [CacheTag.GroupCountV2],
      query: (groupClassRequest) => ({
        body: groupClassRequest,
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.POST,
        responseHandler: 'text',
        url: urlConstants.groupClasses.createGroupV2Url,
      }),
    }),

    editGroupV2: build.mutation<
      string,
      { groupId: string; groupClassRequest: GroupFormState }
    >({
      invalidatesTags: [CacheTag.GroupCountV2, CacheTag.GroupDetails],
      query: ({ groupId, groupClassRequest }) => ({
        body: groupClassRequest,
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.PATCH,
        responseHandler: 'text',
        url: urlConstants.groupClasses.editGroupV2Url(groupId),
      }),
    }),
    getStatusOptions: build.query<GroupStatusOptionsResponse[], void>({
      query: () => urlConstants.groupClasses.getStatusOptions,
    }),

    getGroupClassDetailsV2: build.query<GroupDetailsV2Response, string>({
      providesTags: [CacheTag.GroupDetails],
      queryFn: async (
        groupClassExternalId,
        { dispatch },
        _extraOptions,
        baseQuery,
      ) => {
        const mainResult = await baseQuery({
          url: urlConstants.groupClasses.getGroupDetailsV2(
            groupClassExternalId,
          ),
        });

        if (mainResult.error) return { error: mainResult.error };

        const [programsResult, modalitiesResult, groupTypesResult] =
          await Promise.all([
            dispatch(
              enumerationApi.endpoints.getTreatmentPrograms.initiate(
                undefined,
                {
                  forceRefetch: false,
                  subscribe: false,
                },
              ),
            ),
            dispatch(
              enumerationApi.endpoints.getTreatmentModalitiesEnum.initiate(
                undefined,
                { forceRefetch: false, subscribe: false },
              ),
            ),
            dispatch(
              enumerationApi.endpoints.getGroupTypesEnum.initiate(undefined, {
                forceRefetch: false,
                subscribe: false,
              }),
            ),
          ]);

        const programs = programsResult.data ?? [];
        const modalities = modalitiesResult.data ?? [];
        const groupTypes = groupTypesResult.data ?? [];

        const details = mainResult.data as GroupDetailsResponse;
        const groupType =
          groupTypes.find(
            (groupType) => groupType.code === details.groupTypeCode,
          )?.type ?? details.groupTypeCode;

        const { treatmentPrograms, treatmentModalities, attendeeRoles } =
          enrichGroupWithEnumerations(
            details,
            programs,
            modalities,
            groupTypes,
          );

        const formattedDetails: GroupDetailsV2Response = {
          ...details,
          groupType,
          treatmentPrograms,
          treatmentModalities,
          attendeeRoles,
          ageRange: formattedAgeRange(details?.ageRanges),
          diagnoses: details?.diagnoses ?? [],
          sectionsCount: details?.sections?.length ?? 0,
          isSensitive: details?.sensitive ?? false,
        };

        return {
          data: formattedDetails,
        };
      },
    }),

    getGroupClassDetailsV2Raw: build.query<GroupDetailsResponse, string>({
      providesTags: [CacheTag.GroupDetails],
      query: (groupClassExternalId) => ({
        url: urlConstants.groupClasses.getGroupDetailsV2(groupClassExternalId),
      }),
    }),

    updateGroupStatus: build.mutation<
      void,
      { groupId: string; groupStatus: GroupStatusType; archiveReason?: string }
    >({
      invalidatesTags: [CacheTag.GroupCountV2, CacheTag.GroupClassList],
      query: ({ groupId, groupStatus, archiveReason }) => ({
        body: { groupStatus, archiveReason },
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.PATCH,
        url: urlConstants.groupClasses.updateGroupStatus(groupId),
      }),
    }),

    getGroupSessionDetails: build.query<
      GroupSection,
      { groupId: string; sectionId: string }
    >({
      providesTags: (_, __, { groupId, sectionId }) => [
        {
          type: CacheTag.GroupSessionDetails,
          id: `${groupId}:${sectionId}`,
        },
      ],
      query: ({ groupId, sectionId }) =>
        urlConstants.groupClasses.getGroupSessionDetails(groupId, sectionId),
    }),

    getAllGroupSessions: build.query<GroupSection[], string>({
      query: (groupId) =>
        urlConstants.groupClasses.getAllGroupSessions(groupId),
    }),

    getFrequencyOptions: build.query<GroupClassFrequencyOption[], void>({
      keepUnusedDataFor: GROUP_CLASS_CACHE_TIME,
      query: () => urlConstants.groupClasses.getFrequencyOptions,
    }),
    getSessionOptions: build.query<GroupClassSessionOption[], void>({
      keepUnusedDataFor: GROUP_CLASS_CACHE_TIME,
      query: () => urlConstants.groupClasses.getSessionOptions,
    }),
    createGroupSession: build.mutation<string, CreateSectionRequest>({
      invalidatesTags: [CacheTag.GroupDetails, CacheTag.GroupCountV2],
      query: (body) => ({
        url: urlConstants.groupClasses.createGroupSession(body.groupId),
        method: 'POST',
        responseHandler: 'text',
        body,
      }),
    }),
    editGroupSession: build.mutation<string, CreateSectionRequest>({
      invalidatesTags: (_, __, { groupId, sectionId }) => [
        { type: CacheTag.GroupSessionDetails, id: `${groupId}:${sectionId}` },
        { type: CacheTag.GroupDetails, id: groupId },
        CacheTag.GroupCountV2,
      ],
      query: ({ groupId, sectionId, ...body }) => ({
        url: urlConstants.groupClasses.editGroupSession(groupId, sectionId),
        method: 'PATCH',
        responseHandler: 'text',
        body,
      }),
    }),

    getGroupEventsV2: build.query<
      GroupEventV2[],
      {
        startDateTime: string;
        endDateTime?: string;
        pageSize?: number;
        patientExternalId?: string;
        providerExternalId?: string;
        showDetailed?: boolean;
      }
    >({
      providesTags: [
        CacheTag.GroupClassEventsV2,
        CacheTag.PatientAndSupportGroups,
      ],
      query: ({
        startDateTime,
        endDateTime,
        pageSize = 50,
        patientExternalId,
        providerExternalId,
        showDetailed,
      }) => ({
        params: {
          ['filter-by']: joinQueryParams({
            ['start-date']: startDateTime,
            ...(endDateTime && { ['end-date']: endDateTime }),
          }),
          ['page-size']: pageSize,
          ['patient-external-id']: patientExternalId,
          ['provider-external-id']: providerExternalId,
          ['show-detailed']: showDetailed,
        },
        url: urlConstants.groupClasses.getGroupEventsV2,
      }),
      transformResponse: (response: GroupEventV2[]) => {
        return response;
      },
    }),

    getGroupSectionEventDetails: build.query<GroupEventV2, string>({
      query: (groupSectionEventExternalId) => ({
        url: urlConstants.groupClasses.getGroupSectionEventDetails(
          groupSectionEventExternalId,
        ),
      }),
    }),

    cancelSection: build.mutation<
      void,
      { groupId: string; sectionExternalId: string }
    >({
      invalidatesTags: [CacheTag.GroupDetails, CacheTag.GroupCountV2],
      query: ({ groupId, sectionExternalId }) => ({
        method: HttpRequestMethod.DELETE,
        url: urlConstants.calendar.cancelSection(groupId, sectionExternalId),
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
      }),
    }),

    getRegistrants: build.query<GetRegistrantsV2Response[], string>({
      providesTags: (_, __, groupSectionEventExternalId) => [
        {
          type: CacheTag.GroupSectionEventConfirmedAttendees,
          id: groupSectionEventExternalId,
        },
      ],
      query: (groupSectionEventExternalId) =>
        urlConstants.groupClasses.getGroupEventRegistrants(
          groupSectionEventExternalId,
        ),
    }),

    getGroupSectionEventDates: build.query<
      { nextEventStartDate?: string; lastEventStartDate?: string },
      GetGroupSectionEventDatesParams
    >({
      query: ({
        providerExternalId,
        patientExternalId,
        startDate,
        endDate,
      }) => {
        const filterBy = joinQueryParams({
          ['start-date']: startDate,
          ['end-date']: endDate,
        });

        return {
          url: urlConstants.groupClasses.getGroupSectionEventDates,
          params: {
            'filter-by': filterBy,
            ...(providerExternalId && {
              ['provider-external-id']: providerExternalId,
            }),
            ...(patientExternalId && {
              ['patient-external-id']: patientExternalId,
            }),
          },
        };
      },
    }),
  }),
});

export const {
  useUnregisterForGroupClassMutation,
  useCancelGroupEventMutation,
  useCreateGroupClassMutation,
  useEditGroupClassMutation,
  useGetArchiveReasonsListQuery,
  useGetGroupClassEventsQuery,
  useGetGroupEventsV2Query,
  useGetGroupClassEventDetailsQuery,
  useGetGroupClassEventConfirmedAttendeesQuery,
  useUpdateGroupClassEventAttendeesMutation,
  useArchiveGroupClassMutation,
  useLazyGetGroupClassEventsQuery,
  useLazyGetGroupEventsV2Query,
  useLazyGetActiveOrArchivedGroupClassesListQuery,
  useLazyGetGroupClassDetailQuery,
  useGetGroupClassCountsQuery,
  useGetGroupClassCountsV2Query,
  useLazyGetMyGroupClassesListQuery,
  useGetActiveOrArchiveGroupsV2Query,
  useLazyGetActiveOrArchiveGroupsV2Query,
  useRegisterForGroupClassMutation,
  useGetGroupClassEventMeetingDetailsMutation,
  useGetGroupSectionEventMeetingDetailsV2Mutation,
  useUpdateGroupSectionEventV2Mutation,
  useDeclineEventMutation,
  useCancelEventMutation,
  useAddHostsMutation,
  useRemoveGroupClassEventMutation,
  useCreateGroupV2Mutation,
  useEditGroupV2Mutation,
  useGetStatusOptionsQuery,
  useGetGroupClassDetailsV2Query,
  useGetGroupClassDetailsV2RawQuery,
  useGetFrequencyOptionsQuery,
  useGetSessionOptionsQuery,
  useCreateGroupSessionMutation,
  useEditGroupSessionMutation,
  useGetGroupSessionDetailsQuery,
  useGetAllGroupSessionsQuery,
  useGetMySectionsListQuery,
  useUpdateGroupStatusMutation,
  useCancelSectionMutation,
  useGetRegistrantsQuery,
  useGetGroupSectionEventDetailsQuery,
  useGetGroupSectionEventDatesQuery,
  useUpdateGroupSectionEventAttendeesMutation,
} = groupClassApi;
